import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

import { useAccessControl } from '@communities/access-control-layer/composables/useAccessControl'
import { useLogger } from '@common/core-layer/composables/useLogger'
import { useCurrentUser } from '@common/current-user-layer/composables/useCurrentUser'

import { ChannelSectionsService } from '../services/channel-sections.service'
import { ChannelsService } from '../services/channels.service'
import { ChannelRoles, ChannelVisibility } from '../types'
import { channelsQueryKeys as queryKeys } from '../utils/query-keys.utils'
import {
  addChannelMembersToCache,
  removeChannelMemberFromCache,
  rollbackChannelMembersQueries,
  snapshotChannelMembersQueries,
  updateChannelMemberRoleInCache,
} from '../utils/channel-members-update-helper'
import { updateChannelInSectionsResponse } from '../utils/channel-sections-cache.helper'
import {
  createEmptyChannelSectionsResponse,
  normalizeChannelSectionChannel,
  normalizeChannelsWithSectionsResponse,
} from '../utils/channel-sections-response.utils'

import type {
  ChannelMetadata,
  ChannelSequenceItem,
  CreateChannelInput,
  IChannel,
  IChannelMemberComposit,
  IChannelStatsResponse,
  IChannelsWithSectionsResponse,
  MoveChannelMutationInput,
  PaginationParams,
  UpdateChannelAccessSettingsInput,
  UpdateChannelInput,
  UpdateChannelMembershipSettingsInput,
} from '../types'

const applyOptimisticChannelPlacement = (
  response: IChannelsWithSectionsResponse,
  placement: MoveChannelMutationInput
): IChannelsWithSectionsResponse => {
  if (!placement.targetOrderedIds.includes(placement.channelId)) {
    return response
  }

  const isRootTarget = placement.targetSectionId === null
  const targetOrderMap = new Map(placement.targetOrderedIds.map((id, index) => [id, index + 1]))

  return {
    ...response,
    sections: response.sections.map(section => {
      const sectionId = section._id || ''
      const targetOrder = targetOrderMap.get(sectionId)

      if (!targetOrder || (section.parentSectionId ?? null) !== placement.targetSectionId) {
        return section
      }

      return {
        ...section,
        sequence: isRootTarget ? targetOrder : section.sequence,
        orderInSection: isRootTarget ? null : targetOrder,
      }
    }),
    channels: response.channels.map(channel => {
      const channelId = channel._id || ''
      const isMovedChannel = channelId === placement.channelId
      const targetOrder = targetOrderMap.get(channelId)
      const channelSectionId = isMovedChannel ? placement.targetSectionId : (channel.sectionId ?? null)

      if (!targetOrder || channelSectionId !== placement.targetSectionId) {
        return channel
      }

      return {
        ...channel,
        sectionId: isMovedChannel ? placement.targetSectionId : channel.sectionId,
        sequence: isRootTarget ? targetOrder : null,
        orderInSection: isRootTarget ? null : targetOrder,
      }
    }),
  }
}

const upsertCreatedChannelInSectionsResponse = (
  response: IChannelsWithSectionsResponse,
  channel: IChannel & { _id: string }
): IChannelsWithSectionsResponse => {
  const normalizedChannel = normalizeChannelSectionChannel({
    ...channel,
    id: channel._id,
    sectionId: null,
    sequence: null,
    orderInSection: null,
  })

  const existingIndex = response.channels.findIndex(existingChannel => existingChannel._id === normalizedChannel._id)

  if (existingIndex === -1) {
    return {
      ...response,
      channels: [...response.channels, normalizedChannel],
    }
  }

  return {
    ...response,
    channels: response.channels.map(existingChannel =>
      existingChannel._id === normalizedChannel._id ? { ...existingChannel, ...normalizedChannel } : existingChannel
    ),
  }
}

/**
 * Channels Query Store - Handles fetching channels data using TanStack Query
 * Focused on fetching channels list and metadata with proper access control
 */
export const useChannelsQueryStore = (groupId: MaybeRefOrGetter<string | undefined>) => {
  const service = new ChannelsService()
  const channelSectionsService = new ChannelSectionsService()
  const queryClient = useQueryClient()
  const { store: currentUserStore } = useCurrentUser()
  const accessControl = useAccessControl({
    resourceType: 'channels',
    groupId,
    requireActiveMembership: true,
  })
  const { logger } = useLogger()
  const getCanonicalChannelsListKey = (gId?: string) => queryKeys.list(gId)

  // ===== CHANNELS LIST QUERY =====
  const channelsListQuery = () => {
    const query = useQuery<IChannelsWithSectionsResponse, Error, IChannel[]>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        return getCanonicalChannelsListKey(gId)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        if (!gId) {
          throw new Error('Group ID is required for fetching channels')
        }
        const locationId = accessControl.currentGroup.value?.locationId
        if (!locationId) {
          throw new Error('Location ID is required for fetching channels')
        }

        const response = await channelSectionsService.fetchChannelsWithSections(gId, locationId, false)

        return normalizeChannelsWithSectionsResponse(response)
      },
      select: response => response.channels,
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      enabled: computed(() => accessControl.canCallApi.value),
      refetchOnWindowFocus: false,
    })
    return {
      ...query,
      canAccess: accessControl.canAccess,
      isChecking: accessControl.isChecking,
    }
  }

  // ===== CHANNELS LIST PREFETCH (bypasses access control) =====
  //
  // ⚠️ Deliberately bypasses `useAccessControl` gating. Intended only for
  // cross-app share flows where:
  //   - the user is already authenticated upstream (e.g. meet shell auth),
  //   - the picker pre-filters to groups the user is a member of (see
  //     `useCommunityUser.fetchUserGroups`), so we can't surface a private
  //     group the user has no access to.
  //
  // Do NOT call this from inside a community route — use `channelsListQuery`
  // instead so access control runs. Writes to the same canonical list cache
  // key so consumers using `channelsListQuery` read it immediately, even if
  // their own `enabled` predicate stays false.
  const prefetchChannelsListWithoutAccessControl = async (gId: string, locationId: string): Promise<IChannelsWithSectionsResponse> => {
    return queryClient.fetchQuery<IChannelsWithSectionsResponse>({
      queryKey: getCanonicalChannelsListKey(gId),
      queryFn: async () => {
        const response = await channelSectionsService.fetchChannelsWithSections(gId, locationId, false)
        return normalizeChannelsWithSectionsResponse(response)
      },
      staleTime: 5 * 60 * 1000,
    })
  }

  // ===== CHANNELS METADATA QUERY =====
  const channelsMetadataQuery = () => {
    const query = useQuery<ChannelMetadata, Error>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        return queryKeys.metadata(gId)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        if (!gId) {
          throw new Error('Group ID is required for fetching channels metadata')
        }
        const locationId = accessControl.currentGroup.value?.locationId
        if (!locationId) {
          throw new Error('Location ID is required for fetching channels metadata')
        }
        return service.fetchChannelsMetadata(gId, locationId)
      },
      staleTime: 2 * 60 * 1000, // 2 minutes for metadata
      gcTime: 5 * 60 * 1000,
      enabled: computed(() => {
        // Only fetch metadata on client side to avoid blocking SSR
        return process.client && accessControl.canCallApi.value
      }),
      refetchOnWindowFocus: false,
    })
    return {
      ...query,
      canAccess: accessControl.canAccess,
      isChecking: accessControl.isChecking,
    }
  }

  // ===== CHANNEL BY ID QUERY (e.g. for mention popovers) =====
  const getChannelByIdQuery = (
    channelId: MaybeRefOrGetter<string | undefined>,
    locationId?: MaybeRefOrGetter<string | undefined>,
    enabled?: MaybeRefOrGetter<boolean>
  ) =>
    useQuery<IChannel, Error>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const chId = toValue(channelId)
        return queryKeys.detail(gId, chId)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const chId = toValue(channelId)
        const locationIdValue = locationId !== undefined ? toValue(locationId) : accessControl.currentGroup.value?.locationId
        if (!gId || !chId) {
          throw new Error('Group ID and Channel ID are required for fetching channel detail')
        }
        if (!locationIdValue) {
          throw new Error('Location ID is required for fetching channel detail')
        }
        return service.getChannelById(chId, gId, locationIdValue)
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      enabled: computed(() => {
        const enabledOverride = enabled !== undefined ? toValue(enabled) : true

        if (!enabledOverride || !toValue(channelId)) {
          return false
        }

        const locationIdValue = locationId !== undefined ? toValue(locationId) : accessControl.currentGroup.value?.locationId
        if (!locationIdValue) {
          return false
        }

        if (locationId !== undefined) {
          return !!toValue(groupId) && !accessControl.isChecking.value && accessControl.canAccess.value
        }

        return accessControl.canCallApi.value
      }),
      refetchOnWindowFocus: false,
    })

  const fetchChannelStatsQuery = (
    channelId: MaybeRefOrGetter<string | undefined>,
    locationId?: MaybeRefOrGetter<string | undefined>,
    enabled?: MaybeRefOrGetter<boolean>
  ) =>
    useQuery<IChannelStatsResponse, Error>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const chId = toValue(channelId)
        const locationIdValue = locationId !== undefined ? toValue(locationId) : accessControl.currentGroup.value?.locationId
        return queryKeys.stats(gId, chId, locationIdValue)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const chId = toValue(channelId)
        const locationIdValue = locationId !== undefined ? toValue(locationId) : accessControl.currentGroup.value?.locationId
        if (!gId || !chId) {
          throw new Error('Group ID and Channel ID are required for fetching channel stats')
        }
        if (!locationIdValue) {
          throw new Error('Location ID is required for fetching channel stats')
        }
        return service.getChannelStats(chId, gId, locationIdValue)
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      enabled: computed(() => {
        const enabledOverride = enabled !== undefined ? toValue(enabled) : true

        if (!enabledOverride || !toValue(channelId)) {
          return false
        }

        const locationIdValue = locationId !== undefined ? toValue(locationId) : accessControl.currentGroup.value?.locationId
        if (!locationIdValue) {
          return false
        }

        if (locationId !== undefined) {
          return !!toValue(groupId) && !accessControl.isChecking.value && accessControl.canAccess.value
        }

        return accessControl.canCallApi.value
      }),
      refetchOnWindowFocus: false,
    })

  // ===== MUTATIONS =====

  // Create channel mutation
  const createChannelMutation = useMutation({
    mutationFn: async (data: CreateChannelInput) => {
      const context = accessControl.assertCanCallApi('creating channel')
      return service.create({
        ...data,
        groupId: context.groupId,
        locationId: context.locationId,
      })
    },
    onSuccess: (channel: IChannel) => {
      const gId = toValue(groupId)
      const canonicalListKey = getCanonicalChannelsListKey(gId)
      const currentCanonicalList = queryClient.getQueryData<IChannelsWithSectionsResponse>(canonicalListKey)

      if (!channel._id || !currentCanonicalList) {
        queryClient.invalidateQueries({
          queryKey: canonicalListKey,
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.metadata(gId),
        })
        return
      }

      // The creator of a private channel is automatically its manager. The
      // POST /channels response doesn't always include `userRole`, so default
      // it here for private channels so the "Add Member" button (gated by
      // `channel.userRole === MANAGER` in ChannelSettingsIndex) renders
      // immediately after creation — matching the post-refresh state from
      // GET /channels.
      const userRole = channel.visibility === ChannelVisibility.PRIVATE ? (channel.userRole ?? ChannelRoles.MANAGER) : channel.userRole

      const createdChannel: IChannel & { _id: string } = {
        ...channel,
        _id: channel._id,
        userRole,
        createdBy: {
          ...channel.createdBy,
          fullName: currentUserStore.profile?.fullName || channel.createdBy.fullName,
          avatar: currentUserStore.profile?.avatar || channel.createdBy.avatar,
          slug: currentUserStore.profile?.slug || channel.createdBy.slug,
          title: currentUserStore.profile?.title || channel.createdBy.title,
        },
      }

      // Inject the created channel directly into the canonical cache so the UI updates
      // immediately instead of waiting for the list refetch path to catch up.
      queryClient.setQueryData<IChannelsWithSectionsResponse>(
        canonicalListKey,
        upsertCreatedChannelInSectionsResponse(currentCanonicalList, createdChannel)
      )

      queryClient.invalidateQueries({
        queryKey: queryKeys.metadata(gId),
      })
    },
    onError: (error: unknown) => {
      console.error('Failed to create channel:', error)
    },
  })

  // Update channel mutation
  const updateChannelMutation = useMutation<
    IChannel,
    Error,
    UpdateChannelInput,
    { previousList?: IChannelsWithSectionsResponse; listKey: ReturnType<typeof queryKeys.list> }
  >({
    mutationFn: async (data: UpdateChannelInput) => {
      const context = accessControl.assertCanCallApi('updating channel')
      return service.update({
        ...data,
        groupId: context.groupId,
        locationId: context.locationId,
      })
    },
    onMutate: async (data: UpdateChannelInput) => {
      const gId = toValue(groupId)
      const listKey = getCanonicalChannelsListKey(gId)

      await queryClient.cancelQueries({
        queryKey: listKey,
      })

      const previousList = queryClient.getQueryData<IChannelsWithSectionsResponse>(listKey)

      queryClient.setQueryData<IChannelsWithSectionsResponse>(listKey, oldData => {
        if (!oldData) {
          return oldData
        }

        return updateChannelInSectionsResponse(oldData, data._id, data)
      })

      return {
        previousList,
        listKey,
      }
    },
    onSuccess: (channel: IChannel, _variables, context) => {
      if (!channel._id || !context?.listKey) {
        return
      }

      const updatedChannel: IChannel & { _id: string } = {
        ...channel,
        _id: channel._id,
      }

      queryClient.setQueryData<IChannelsWithSectionsResponse>(context.listKey, oldData => {
        if (!oldData) {
          return oldData
        }

        return updateChannelInSectionsResponse(oldData, updatedChannel._id, updatedChannel)
      })
    },
    onError: (error: unknown, _variables, context) => {
      if (context?.listKey) {
        queryClient.setQueryData(context.listKey, context.previousList)
      }

      console.error('Failed to update channel:', error)
    },
  })

  // Delete channel mutation
  const deleteChannelMutation = useMutation<
    void,
    Error,
    string,
    { previousList?: IChannelsWithSectionsResponse; listKey: ReturnType<typeof queryKeys.list> } | undefined
  >({
    mutationFn: async (channelId: string) => {
      const context = accessControl.assertCanCallApi('deleting channel')
      return service.deleteChannel(channelId, context.groupId, context.locationId)
    },
    onMutate: async (channelId: string) => {
      const gId = toValue(groupId)
      const listKey = getCanonicalChannelsListKey(gId)

      await queryClient.cancelQueries({
        queryKey: listKey,
      })

      const previousList = queryClient.getQueryData<IChannelsWithSectionsResponse>(listKey)

      queryClient.setQueryData<IChannelsWithSectionsResponse>(listKey, oldData => {
        if (!oldData) {
          return oldData
        }

        return {
          ...oldData,
          channels: oldData.channels.filter(channel => channel._id !== channelId),
        }
      })

      return {
        previousList,
        listKey,
      }
    },
    onSuccess: () => {
      const gId = toValue(groupId)
      // Invalidate channels list query
      queryClient.invalidateQueries({
        queryKey: getCanonicalChannelsListKey(gId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.metadata(gId),
      })
    },
    onError: (error: unknown, _channelId, context) => {
      if (context?.listKey) {
        queryClient.setQueryData(context.listKey, context.previousList)
      }

      console.error('Failed to delete channel:', error)
    },
  })

  // ===== CHANNEL MEMBERS QUERY =====
  /**
   * Factory function for fetching channel members with pagination
   * Uses infinite query for better pagination handling
   */
  const channelMembersQuery = (
    channelId: MaybeRefOrGetter<string | undefined>,
    roles?: MaybeRefOrGetter<ChannelRoles[] | undefined>,
    searchText?: MaybeRefOrGetter<string | undefined>,
    pageSize: MaybeRefOrGetter<number> = 10
  ) => {
    const infiniteQuery = useInfiniteQuery<IChannelMemberComposit[], Error>({
      queryKey: computed(() => {
        const cId = toValue(channelId)
        const gId = toValue(groupId)
        const rolesValue = toValue(roles)
        const searchValue = toValue(searchText)
        return queryKeys.members(gId, cId, rolesValue?.join(','), searchValue)
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const cId = toValue(channelId)
        const gId = toValue(groupId)
        const rolesValue = toValue(roles)
        const searchValue = toValue(searchText)
        const limit = toValue(pageSize)

        if (!cId) {
          throw new Error('Channel ID is required for fetching channel members')
        }
        if (!gId) {
          throw new Error('Group ID is required for fetching channel members')
        }

        const locationId = accessControl.currentGroup.value?.locationId
        if (!locationId) {
          throw new Error('Location ID is required for fetching channel members')
        }

        const paginationParams: PaginationParams = {
          limit,
          pageNo: pageParam as number,
          offset: ((pageParam as number) - 1) * limit,
        }

        const members = await service.fetchChannelMembers(cId, gId, locationId, rolesValue?.join(','), paginationParams, searchValue)

        return members
      },
      getNextPageParam: (lastPage, allPages) => {
        const limit = toValue(pageSize)

        // If last page is empty, definitely no more pages
        if (lastPage.length === 0) {
          return undefined
        }

        // If last page has fewer items than limit AND we've already fetched at least one page,
        // assume no more pages (standard pagination pattern)
        // BUT: if it's the first page and has fewer items, still try next page once
        // (API might return fewer items due to filtering but still have more data)
        if (lastPage.length < limit && allPages.length > 1) {
          return undefined
        }

        // Continue fetching: either last page has exactly limit items, or it's the first page with fewer items
        const nextPage = allPages.length + 1

        return nextPage
      },
      initialPageParam: 1,
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 5 * 60 * 1000,
      enabled: computed(() => {
        const cId = toValue(channelId)
        return !!cId && accessControl.canCallApi.value
      }),
      refetchOnWindowFocus: false,
    })

    // Add computed allData property that flattens all pages
    const allData = computed(() => {
      if (!infiniteQuery.data?.value?.pages) return []
      return infiniteQuery.data.value.pages.flat()
    })

    return {
      ...infiniteQuery,
      allData,
    }
  }

  // Update member role mutation
  const updateMemberRoleMutation = useMutation({
    mutationFn: async ({ channelId, contactId, role }: { channelId: string; contactId: string; role: ChannelRoles }) => {
      const context = accessControl.assertCanCallApi('updating channel member role')
      return service.updateMemberRole(channelId, contactId, role, context.groupId, context.locationId)
    },
    onMutate: async ({ channelId, contactId, role }) => {
      const gId = toValue(groupId)
      if (!gId) return

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.members(gId, channelId),
      })

      // Snapshot previous state for rollback
      const previousQueries = snapshotChannelMembersQueries(queryClient, gId, channelId)

      // Optimistically update member role in cache
      updateChannelMemberRoleInCache(queryClient, gId, channelId, contactId, role)

      return { previousQueries }
    },
    onSuccess: (_data, variables) => {
      const gId = toValue(groupId)
      // Optionally refetch to ensure consistency
      queryClient.invalidateQueries({
        queryKey: queryKeys.members(gId, variables.channelId),
      })
    },
    onError: (error: unknown, _variables, context) => {
      if (!context?.previousQueries) return

      // Rollback to previous state
      rollbackChannelMembersQueries(queryClient, context.previousQueries)

      console.error('Failed to update member role:', error)
    },
  })

  // Remove member from channel mutation
  const removeMemberMutation = useMutation({
    mutationFn: async ({ channelId, contactId }: { channelId: string; contactId: string }) => {
      const context = accessControl.assertCanCallApi('removing channel member')
      return service.removeUserFromChannel(channelId, contactId, context.groupId, context.locationId)
    },
    onMutate: async ({ channelId, contactId }) => {
      const gId = toValue(groupId)
      if (!gId) return

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.members(gId, channelId),
      })

      // Snapshot previous state for rollback
      const previousQueries = snapshotChannelMembersQueries(queryClient, gId, channelId)

      // Optimistically remove member from cache
      removeChannelMemberFromCache(queryClient, gId, channelId, contactId)

      return { previousQueries }
    },
    onSuccess: (_data, variables) => {
      const gId = toValue(groupId)
      if (!gId) return

      // Invalidate members query to refetch and get updated member list
      queryClient.invalidateQueries({
        queryKey: queryKeys.members(gId, variables.channelId),
      })

      // Invalidate suggestions query to add removed member back to suggestions list
      queryClient.invalidateQueries({
        queryKey: queryKeys.channelSuggestions(gId, variables.channelId),
      })

      // Invalidate channels list query to update channel.membersCount
      queryClient.invalidateQueries({
        queryKey: getCanonicalChannelsListKey(gId),
      })

      // Invalidate metadata to update member counts (though already updated optimistically)
      queryClient.invalidateQueries({
        queryKey: queryKeys.metadata(gId),
      })
    },
    onError: (error: unknown, _variables, context) => {
      if (!context?.previousQueries) return

      // Rollback to previous state
      rollbackChannelMembersQueries(queryClient, context.previousQueries)

      console.error('Failed to remove member:', error)
    },
  })

  // Add members to channel mutation
  const addChannelMembersMutation = useMutation({
    mutationFn: async ({
      members,
      channelId,
    }: {
      members: IChannelMemberComposit[] // Full member objects with contactId, role, and all other details
      channelId: string
    }) => {
      const context = accessControl.assertCanCallApi('adding channel members')
      // Extract membersData (contactId + role) from full member objects for API call
      const membersData = members.map(member => ({
        contactId: member.contactId,
        role: member.role,
        notify: true,
      }))
      return service.addChannelMembers(membersData, channelId, context.groupId, context.locationId)
    },
    onMutate: async ({ members, channelId }) => {
      const gId = toValue(groupId)
      if (!gId) return

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.members(gId, channelId),
      })

      // Snapshot previous state for rollback
      const previousQueries = snapshotChannelMembersQueries(queryClient, gId, channelId)

      // Use full member objects directly for optimistic updates (no need to create from cache)
      addChannelMembersToCache(queryClient, gId, channelId, members)

      return { previousQueries }
    },
    onSuccess: (_, variables) => {
      const gId = toValue(groupId)
      if (!gId) return

      // Invalidate members query to refetch and get proper member data with names
      queryClient.invalidateQueries({
        queryKey: queryKeys.members(gId, variables.channelId),
      })

      // Invalidate suggestions query to remove added members from suggestions list
      queryClient.invalidateQueries({
        queryKey: queryKeys.channelSuggestions(gId, variables.channelId),
      })

      // Invalidate channels list query to update channel.membersCount
      queryClient.invalidateQueries({
        queryKey: getCanonicalChannelsListKey(gId),
      })

      // Invalidate metadata to update member counts
      queryClient.invalidateQueries({
        queryKey: queryKeys.metadata(gId),
      })
    },
    onError: (error: unknown, _variables, context) => {
      if (!context?.previousQueries) return

      // Rollback to previous state
      rollbackChannelMembersQueries(queryClient, context.previousQueries)

      console.error('Failed to add channel members:', error)
    },
  })

  // ===== CHANNEL SUGGESTIONS QUERY =====
  /**
   * Get group members for channel suggestions
   * Returns users who can be added to a specific channel
   * Uses infinite query for pagination support
   */
  const channelSuggestionsQuery = (
    channelId: MaybeRefOrGetter<string | undefined>,
    params?: MaybeRefOrGetter<{ limit?: number; skip?: number; search?: string }>
  ) => {
    const infiniteQuery = useInfiniteQuery<IChannelMemberComposit[], Error>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const chId = toValue(channelId)
        const p = toValue(params)
        const searchValue = p?.search
        return queryKeys.channelSuggestions(gId, chId, searchValue)
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const gId = toValue(groupId)
        const chId = toValue(channelId)
        const p = toValue(params)
        const limit = p?.limit || 10

        if (!gId || !chId) {
          throw new Error('Group ID and Channel ID are required for fetching channel suggestions')
        }
        const locationId = accessControl.currentGroup.value?.locationId
        if (!locationId) {
          throw new Error('Location ID is required for fetching channel suggestions')
        }

        const paginationParams = {
          limit,
          skip: ((pageParam as number) - 1) * limit,
          search: p?.search,
        }

        return service.getGroupMembersForChannelSuggestions(gId, locationId, chId, paginationParams)
      },
      getNextPageParam: (lastPage, allPages) => {
        // If last page has fewer items than limit, there are no more pages
        const p = toValue(params)
        const limit = p?.limit || 10
        if (lastPage.length < limit) {
          return undefined
        }
        return allPages.length + 1
      },
      initialPageParam: 1,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      enabled: computed(() => {
        const chId = toValue(channelId)
        return !!chId && accessControl.canCallApi.value
      }),
      refetchOnWindowFocus: false,
    })

    // Add computed allData property that flattens all pages
    const allData = computed(() => {
      if (!infiniteQuery.data?.value?.pages) return []
      return infiniteQuery.data.value.pages.flat()
    })

    return {
      ...infiniteQuery,
      allData,
    }
  }

  // Leave channel mutation
  const leaveChannelMutation = useMutation({
    mutationFn: async (channelId: string) => {
      const context = accessControl.assertCanCallApi('leaving channel')
      return service.leaveChannel(channelId, context.groupId, context.locationId)
    },
    onSuccess: () => {
      const gId = toValue(groupId)
      // Invalidate channels list to remove the channel from user's list
      queryClient.invalidateQueries({
        queryKey: getCanonicalChannelsListKey(gId),
      })
      // Also invalidate metadata
      queryClient.invalidateQueries({
        queryKey: queryKeys.metadata(gId),
      })
    },
    onError: (error: unknown) => {
      console.error('Failed to leave channel:', error)
    },
  })

  // Mark channel as read mutation
  const markChannelAsReadMutation = useMutation({
    mutationFn: async (channelId: string) => {
      const context = accessControl.assertCanCallApi('marking channel as read')
      return service.markChannelAsRead(channelId, context.groupId, context.locationId)
    },
    onMutate: async (channelId: string) => {
      const gId = toValue(groupId)
      const metadataQueryKey = queryKeys.metadata(gId)

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: metadataQueryKey })

      // Snapshot the previous value for rollback
      const previousMetadata = queryClient.getQueryData<ChannelMetadata>(metadataQueryKey)

      // Optimistically update the cache
      if (previousMetadata) {
        queryClient.setQueryData<ChannelMetadata>(metadataQueryKey, old => {
          if (!old) return old
          const now = new Date()
          return {
            ...old,
            unreadCounts: {
              ...old.unreadCounts,
              [channelId]: 0, // Reset unread count to 0
            },
            lastReadTimestamps: {
              ...old.lastReadTimestamps,
              [channelId]: now, // Update last read timestamp
            },
          }
        })
      }

      // Return context with previous value for rollback
      return { previousMetadata }
    },
    onSuccess: () => {
      // Optimistic update in onMutate already set the correct final state (unreadCount: 0).
      // Do NOT invalidate metadata here — the refetch races with backend eventual consistency
      // and can overwrite the optimistic 0 with a stale non-zero count.
    },
    onError: (error: Error, _channelId: string, context?: { previousMetadata?: ChannelMetadata }) => {
      const gId = toValue(groupId)
      const metadataQueryKey = queryKeys.metadata(gId)

      // Rollback to previous state on error
      if (context?.previousMetadata) {
        queryClient.setQueryData(metadataQueryKey, context.previousMetadata)
      }

      console.error('Failed to mark channel as read:', error)
    },
  })

  // Update channel sequence mutation
  const updateChannelSequenceMutation = useMutation<
    unknown,
    Error,
    { groupId: string; updatedChannelSequence: ChannelSequenceItem[]; reorderedChannels?: IChannel[] },
    { previousData: IChannelsWithSectionsResponse | undefined; listKey: ReturnType<typeof queryKeys.list> } | undefined
  >({
    mutationFn: async ({ groupId, updatedChannelSequence }) => {
      const context = accessControl.assertCanCallApi('updating channel sequence')
      return service.updateChannelSequence(context.groupId, context.locationId, updatedChannelSequence)
    },
    onMutate: async ({ groupId, reorderedChannels }) => {
      if (!reorderedChannels) return

      const listKey = getCanonicalChannelsListKey(groupId)
      await queryClient.cancelQueries({ queryKey: listKey })
      const previousData = queryClient.getQueryData<IChannelsWithSectionsResponse>(listKey)
      queryClient.setQueryData<IChannelsWithSectionsResponse>(listKey, oldData => {
        const currentData = oldData ?? createEmptyChannelSectionsResponse()

        return {
          ...currentData,
          channels: reorderedChannels.map(channel => normalizeChannelSectionChannel(channel)),
        }
      })

      return { previousData, listKey }
    },
    onSuccess: (_data, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: getCanonicalChannelsListKey(groupId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.metadata(groupId) })
    },
    onError: (error, _variables, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(context.listKey, context.previousData)
      } else if (context?.listKey) {
        queryClient.removeQueries({
          queryKey: context.listKey,
          exact: true,
        })
      }
      console.error('Failed to update channel sequence:', error)
    },
  })

  // Update channel visibility mutation
  const updateChannelVisibilityMutation = useMutation({
    mutationFn: async ({ channelId, visibility }: { channelId: string; visibility: ChannelVisibility }) => {
      const context = accessControl.assertCanCallApi('updating channel visibility')
      return service.updateChannelVisibility(channelId, visibility, context.groupId, context.locationId)
    },
    onSuccess: (_data, { channelId, visibility }) => {
      const gId = toValue(groupId)
      const listKey = getCanonicalChannelsListKey(gId)
      queryClient.setQueryData<IChannelsWithSectionsResponse>(listKey, oldData => {
        if (!oldData) return oldData
        return updateChannelInSectionsResponse(oldData, channelId, { visibility: _data?.visibility ?? visibility })
      })
    },
    onError: (error: unknown) => {
      console.error('Failed to update channel visibility:', error)
    },
  })

  const updateChannelAccessSettingsMutation = useMutation({
    mutationFn: async (data: UpdateChannelAccessSettingsInput) => {
      const context = accessControl.assertCanCallApi('updating channel access settings')
      return service.updateAccessSettings({
        ...data,
        groupId: context.groupId,
        locationId: context.locationId,
      })
    },
    onSuccess: (channel: IChannel, { channelId, visibility, isDiscoverable }) => {
      const gId = toValue(groupId)
      const listKey = getCanonicalChannelsListKey(gId)
      queryClient.setQueryData<IChannelsWithSectionsResponse>(listKey, oldData => {
        if (!oldData) return oldData
        return updateChannelInSectionsResponse(oldData, channelId, {
          visibility: channel.visibility ?? visibility,
          isDiscoverable: channel.isDiscoverable ?? isDiscoverable,
        })
      })
    },
    onError: (error: unknown) => {
      logger.error('Channel access update failed', {
        error: error instanceof Error ? error : new Error(String(error)),
        labels: { operation: 'update channel access settings' },
      })
    },
  })

  const updateChannelMembershipSettingsMutation = useMutation({
    mutationFn: async (data: UpdateChannelMembershipSettingsInput) => {
      const context = accessControl.assertCanCallApi('updating channel membership settings')
      return service.updateMembershipSettings({
        ...data,
        groupId: context.groupId,
        locationId: context.locationId,
      })
    },
    onSuccess: (channel: IChannel, { channelId }) => {
      const gId = toValue(groupId)
      const listKey = getCanonicalChannelsListKey(gId)
      queryClient.setQueryData<IChannelsWithSectionsResponse>(listKey, oldData => {
        if (!oldData) return oldData
        return updateChannelInSectionsResponse(oldData, channelId, {
          ...channel,
          membershipMeta: channel.membershipMeta,
        })
      })

      const detailKey = queryKeys.detail(gId, channelId)
      queryClient.setQueryData<IChannel>(detailKey, previous =>
        previous
          ? {
              ...previous,
              ...channel,
              membershipMeta: channel.membershipMeta,
            }
          : channel
      )
    },
    onError: (error: unknown, { channelId }) => {
      logger.error('Failed to update channel membership settings', {
        error: error instanceof Error ? error : new Error(String(error)),
        labels: { operation: 'update channel membership settings', channelId },
      })
    },
  })

  const joinPaidChannelMutation = useMutation({
    mutationFn: async ({ channelId, orderId, orderSourceId }: { channelId: string; orderId: string; orderSourceId: string }) => {
      const context = accessControl.assertCanCallApi('joining paid channel')
      return service.joinPaidChannel(channelId, context.groupId, context.locationId, {
        orderId,
        orderSourceId,
      })
    },
    onSuccess: (_member, { channelId }) => {
      const gId = toValue(groupId)
      const listKey = getCanonicalChannelsListKey(gId)
      queryClient.setQueryData<IChannelsWithSectionsResponse>(listKey, oldData => {
        if (!oldData) return oldData
        return updateChannelInSectionsResponse(oldData, channelId, { userRole: ChannelRoles.MEMBER })
      })
      // Optimistic role above gives instant access; refetch reconciles with the server like sibling membership mutations.
      void queryClient.invalidateQueries({ queryKey: listKey })
    },
    onError: (error: unknown, { channelId }) => {
      logger.error('[CHANNELS] Failed to join paid channel', {
        error: error instanceof Error ? error : new Error(String(error)),
        labels: { operation: 'join paid channel', channelId },
      })
    },
  })

  const moveChannelMutation = useMutation<
    unknown,
    Error,
    MoveChannelMutationInput,
    { previousData: IChannelsWithSectionsResponse | undefined; listKey: ReturnType<typeof queryKeys.list> } | undefined
  >({
    mutationFn: async placement => {
      const context = accessControl.assertCanCallApi('moving channel')
      return service.moveChannel(context.groupId, context.locationId, placement)
    },
    onMutate: async placement => {
      const gId = toValue(groupId)
      const listKey = getCanonicalChannelsListKey(gId)

      await queryClient.cancelQueries({ queryKey: listKey })
      const previousData = queryClient.getQueryData<IChannelsWithSectionsResponse>(listKey)

      if (previousData) {
        queryClient.setQueryData<IChannelsWithSectionsResponse>(listKey, oldData => {
          if (!oldData) {
            return oldData
          }

          return applyOptimisticChannelPlacement(oldData, placement)
        })
      }

      return { previousData, listKey }
    },
    onSuccess: () => {
      const gId = toValue(groupId)
      queryClient.invalidateQueries({ queryKey: getCanonicalChannelsListKey(gId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.metadata(gId) })
    },
    onError: (error, _variables, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(context.listKey, context.previousData)
      } else if (context?.listKey) {
        queryClient.removeQueries({
          queryKey: context.listKey,
          exact: true,
        })
      }

      console.error('Failed to move channel:', error)
    },
  })

  // ===== PUBLIC API =====
  return {
    // Channels list factory (return includes canAccess, isChecking for this query’s access control)
    channelsListQuery,

    // Prefetch helper for non-community contexts (e.g. meet share-as-post).
    // Bypasses useAccessControl — callsite name reflects that.
    prefetchChannelsListWithoutAccessControl,

    // Channels metadata factory
    channelsMetadataQuery,

    // Channel by ID factory (e.g. for mention popovers)
    getChannelByIdQuery,

    fetchChannelStatsQuery,

    // Channel members factory
    channelMembersQuery,

    // Channel suggestions factory
    channelSuggestionsQuery,

    // Mutations
    createChannelMutation,
    updateChannelMutation,
    updateChannelVisibilityMutation,
    updateChannelAccessSettingsMutation,
    updateChannelMembershipSettingsMutation,
    joinPaidChannelMutation,
    deleteChannelMutation,
    leaveChannelMutation,
    markChannelAsReadMutation,
    updateChannelSequenceMutation,
    moveChannelMutation,
    addChannelMembersMutation,
    updateMemberRoleMutation,
    removeMemberMutation,
  }
}
